import {
  ExtraInfo,
  OnboardingDetailedStep,
  OnboardingStep,
  TradingMethodType,
} from "@shared/features/onboarding/types";
import { EXCHANGES, PRIMARY_EXCHANGES } from "@shared/common/constants";

export const ONBOARDING_STEP_KEY = "onboarding_step";
export const CONNECT_TELEGRAM_KEY = "connect_telegram";

export const STEP_TO_TITLE: { [step in OnboardingStep]: string } = {
  [OnboardingStep.CONNECT_EXCHANGE]: "Connect Exchange",
  [OnboardingStep.TRADING_METHOD]: "Trading Method",
  [OnboardingStep.METHOD_SETTINGS]: "Method Settings",
  [OnboardingStep.OVERVIEW]: "Overview",
};

export const PREVIOUS_STEP_TO_BACK_BUTTON_TITLE: {
  [step in OnboardingDetailedStep]?: string;
} = {
  [OnboardingDetailedStep.SELECT_EXCHANGE]: "Select Exchange",
  [OnboardingDetailedStep.COPY_DCA_BOT]: "Copy a DCA Bot",
  [OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP]: "Follow a Signals Group",
  [OnboardingDetailedStep.TRADING_METHOD]: "Trading Method",
};

export const SKIP_ONBOARDING_TRADING_METHOD =
  "If you skip the onboarding you will be able to follow a group or copy a DCA bot later.";
export const SKIP_ONBOARDING_FOLLOW_GROUP =
  "If you skip the onboarding you will be able to follow a group later from our 'Marketplace'.";
export const MOBILE_SKIP_ONBOARDING_FOLLOW_GROUP_HAS_GROUPS =
  "If you skip the onboarding you will be able to enable Auto-Trading from the 'Accounts & Configuration' page or find a group in the 'Marketplace'.";
export const SKIP_ONBOARDING_SIGNALS_BOT_HAS_GROUPS =
  "If you skip the onboarding you will be able to create signals bot from the 'Signals Bots' page or find a group in the 'Marketplace'.";
export const SKIP_ONBOARDING_DCA_BOT =
  "If you skip the onboarding you can find a DCA Bot to copy on the 'Public Bots' page later.";

export const CONNECT_EXCHANGE_PRIMARY_TITLE = "Connect Your Exchange";
export const SELECT_EXCHANGE_PRIMARY_TITLE = "Select Exchange";
export const DEMO_MODE_CONNECT_EXCHANGE_PRIMARY_TITLE = "Connect Demo Account";

export const API_KEYS_DESCRIPTION =
  "API keys are a unique set of identifiers that allow Cornix to programmatically perform actions on your behalf in your crypto exchange account (Binance, ByBit, etc).";
export const API_KEYS_ARTICLE = "5814843";

export const TRADING_METHOD_PRIMARY_TITLE = "Where do you want to start?";
export const TRADING_METHOD_DESCRIPTION =
  "Choose between a Signals Group or a DCA Bot to start your auto-trading. You can add additional auto-trading methods later.";

export const TRADING_METHOD_TO_TITLE = {
  [TradingMethodType.DCA_BOT]: "Copy a DCA Bot",
  [TradingMethodType.SIGNALS_GROUP]: "Follow a Group",
};
export const TRADING_METHOD_TO_INFO = {
  [TradingMethodType.DCA_BOT]: [
    "Buy Low, Sell High, Automatically.",
    "A wide variety of proven Bots.",
    "Designed to succeed no matter the market conditions.",
  ],
  [TradingMethodType.SIGNALS_GROUP]: [
    "Automatically Copy Expert Traders.",
    "Multiple Symbols & Trading Strategies.",
    "Opens new trades the moment Signals are published.",
  ],
};
export const TRADING_METHOD_TO_EXTRA_INFO = {
  [TradingMethodType.DCA_BOT]:
    "DCA bots will start buying at market price and then will keep on purchasing as the price decreases. The bot will sell at a profit when the price increases and will start a new trading cycle every time a trade is closed.",
  [TradingMethodType.SIGNALS_GROUP]:
    "A group provides signals for when to buy (entry targets) and sell (take-profit targets) a certain cryptocurrency.",
};
export const TRADING_METHOD_TO_ARTICLE = {
  [TradingMethodType.DCA_BOT]: "6166394",
  [TradingMethodType.SIGNALS_GROUP]: "5814976",
};
export const TRADING_METHOD_TO_DESCRIPTION = {
  [TradingMethodType.DCA_BOT]:
    "Our DCA bots are designed to help traders regardless of the market conditions. Start trading now by copying a successful bot.",
  [TradingMethodType.SIGNALS_GROUP]:
    "Start trading with a signal groups created by experienced traders.",
};
export const TRADING_METHOD_TO_FULL_LIST = {
  [TradingMethodType.DCA_BOT]: "Check out all bots in the platform",
  [TradingMethodType.SIGNALS_GROUP]: "Check out our full marketplace",
};
export const TRADING_METHOD_TO_LOCAL_STORAGE_STEP = {
  [TradingMethodType.DCA_BOT]: OnboardingDetailedStep.COPY_DCA_BOT,
  [TradingMethodType.SIGNALS_GROUP]:
    OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP,
};

export const ONBOARDING_GROUP_CARD_WIDTH = 432;
export const MOBILE_ONBOARDING_GROUP_CARD_WIDTH = 330;
export const ONBOARDING_GROUP_CARD_HEIGHT = 234;
export const MOBILE_ONBOARDING_GROUP_CARD_HEIGHT = 250;
export const ONBOARDING_MY_GROUP_CARD_HEIGHT = 154;
export const APP_ONBOARDING_MY_GROUP_CARD_HEIGHT = 146;

export const EXTERNAL_ACCOUNT_WHY_CONNECT_TITLE =
  "Why should I connect an External Account?";
export const EXTERNAL_ACCOUNT_WHY_CONNECT_DESCRIPTION =
  "Connecting your external account will allow us to detect which Telegram channels or Discord servers you are a member of.";
export const WHAT_IS_UUID_TITLE = "What is a UUID?";
export const WHAT_IS_UUID_DESCRIPTION =
  "This unique identifier helps us verify and connect your Telegram account.";
export const UUID_ALREADY_CONNECTED_WARNING_MESSAGE =
  "This Unique Identifier (UUID) is already connected to an existing Cornix Account with a different email address.";

export const CONNECT_EXTERNAL_ACCOUNT_PRIMARY_TITLE =
  "Connect an External Account";

export const BOT_CARD_WIDTH = 432;
export const MOBILE_BOT_CARD_WIDTH = 330;
export const BOT_CARD_HEIGHT = 360;
export const MOBILE_BOT_CARD_HEIGHT = 300;

export const RADIO_SIZE = 16;
export const EXCHANGE_ICON_SIZE = 16;
export const EXCHANGE_ICON_MARGIN = 6;

export const MIN_USD_AMOUNT_PER_TRADE = 10;
export const MIN_BINANCE_USD_AMOUNT_PER_TRADE = 30;
export const MAX_GROUP_CONFIGURATIONS = 3;
export const MAX_ACCOUNTS_ROWS = 3;

export const TRADING_METHOD_TO_OVERVIEW_TITLE = {
  [TradingMethodType.DCA_BOT]: "DCA Bot Overview",
  [TradingMethodType.SIGNALS_GROUP]: "Signals Group Overview",
};

export const TRADING_METHOD_TO_OVERVIEW_DESCRIPTION = {
  [TradingMethodType.DCA_BOT]:
    "We will start a DCA bot for you on this exchange with these settings. You can always add additional trading methods later.",
  [TradingMethodType.SIGNALS_GROUP]:
    "You will start trading with the signal group on this exchange with these settings. You can always add additional trading methods later.",
};

export const BOT_AMOUNT_PER_TRADE_TOOLTIP =
  "The bot will create trades and allocate this amount to each. The trade might not use all of it if the take-profit was reached.";
export const LOW_AMOUNT_WARNING_MESSAGE =
  "Amount is too small - your orders' amount might be lower than the minimum required in the exchange.";

export const UNCHECKED_CHECKBOX_MESSAGE =
  "To continue, please read and confirm.";

export const CONFIRM_AUTO_TRADING_MESSAGE =
  "Cornix does not endorse or recommend any signal providers that are integrated with the platform and will not be responsible for the results when interacting with such third parties using the platform.";

export const FALLBACK_MAIN_COINS = ["USDT", "USD", "BTC", "BUSD", "USDC"];

export const EXCHANGE_TO_CREATE_API_KEYS_PERMISSIONS: Record<string, string> = {
  [EXCHANGES.BINANCE]: "'Spot & Margin'",
  [EXCHANGES.BINANCE_FUTURES]: "'Futures'",
  [EXCHANGES.BINANCE_COIN_FUTURES]: "'Futures'",
  [EXCHANGES.BYBIT_SPOT]: "'Trade' under SPOT",
  [EXCHANGES.BYBIT_SPOT_MARGIN]: "'Trade' under SPOT",
  [EXCHANGES.BYBIT_USDT]: "'Orders' and 'Positions' under Contracts",
  [EXCHANGES.BYBIT_INVERSE]: "'Orders' and 'Positions' under Contracts",
  [EXCHANGES.KUCOIN]: "",
  [EXCHANGES.KUCOIN_FUTURES]: "'Future Trading' &",
  [EXCHANGES.BITGET_SPOT]: "",
  [EXCHANGES.BITGET_FUTURES]: "'Orders' and 'Holdings' under Futures and",
  [EXCHANGES.BINGX_SPOT]: "'Spot Trading'",
  [EXCHANGES.BINGX_FUTURES]: "'Perpetual Futures Trading'",
};

export const PERMISSIONS_PLACEHOLDER = "permissions_placeholder";

export const BINANCE_CREATE_API_KEYS_GUIDE = [
  "Make sure you’ve completed your account verification",
  "In your Binance account, click on the account menu in the upper right corner",
  "Click 'API Management' and then 'Create API'",
  "Choose the API keys type and enter a label ",
  "Go through the security verification",
  "Enable 'Reading' permissions",
  `Enable ${PERMISSIONS_PLACEHOLDER} permissions`,
  "Choose Restrict access to trusted IPs only",
  "Add Cornix's IP addresses to the Binance list of trusted IPs",
  "Copy & Paste the API Key and Secret key",
];
export const BYBIT_SPOT_CREATE_API_KEYS_GUIDE = [
  "Login to ByBit",
  "Click on the user icon in the upper right corner",
  "Click 'API' and then 'Create New Key'",
  "Choose the 'Connect to Third-Party Applications'",
  "select 'Cornix' in the Application name dropdown",
  "Select the 'Read-Write' permissions",
  `Select ${PERMISSIONS_PLACEHOLDER}`,
  "Click 'Submit' and Enter your 2FA code",
  "Copy & Paste the API Key and Secret key",
];
export const KUCOIN_CREATE_API_KEYS_GUIDE = [
  "In your Kucoin account, click on the user icon in the upper right corner",
  "Click 'API Management' and then 'Create API'",
  "Click 'Link Third Party Applications' and choose Cornix from the drop-down list",
  "Enter a name for your API keys",
  "Create a Passphrase (one you’ll remember!)",
  `Check the ${PERMISSIONS_PLACEHOLDER} 'Spot Trading' in the Permission section`,
  "Copy & Paste the API Key and Secret and Enter your API passphrase here",
];
export const OKX_CREATE_API_KEYS_GUIDE = [
  "In your OKX account, click on the user icon in the upper right corner",
  "Click 'API Keys' and then 'Create V5 API key'",
  "Click 'Linking third-party apps' and choose Cornix from the drop-down list.",
  "Enter a name for your API keys",
  "Create a Passphrase (one you’ll remember!)",
  "Check the 'Trade' checkbox in the Permission section and click confirm",
  "Copy & Paste the API Key and Secret and Enter your API passphrase here",
];
export const BITMEX_CREATE_API_KEYS_GUIDE = [
  "Login to your BitMEX account and click 'API' in the main navigation",
  "On the left-hand menu, navigate to 'API Key Management'",
  "Enter an API Name (Leave CIDR blank)",
  "Select the 'Order' key permission",
  "If required enter a 2FA code",
  "Press 'Create API Key'",
  "Copy & paste the API ID and Secret",
];
export const HUOBI_CREATE_API_KEYS_GUIDE = [
  "Login to your Huobi account",
  "Click on the 'Account Icon'  in the upper right corner",
  "Select 'API Management'",
  "Enter a label",
  "Select 'Read' and 'Trade' permissions and click 'Create'",
  "Complete the security authentication",
  "Copy & Paste the Access Key and Secret key",
];
export const BITGET_CREATE_API_KEYS_GUIDE = [
  "Make sure you’ve completed your Bitget account verification",
  "In your Bitget account, click on the user icon in the upper right corner",
  "Navigate to 'API keys' and click on 'Create API Key'",
  "Select System-generated API Key",
  "Enter a name for your API keys",
  "Create a Passphrase (one you’ll remember!)",
  "Select 'Read-write' permissions",
  `Check ${PERMISSIONS_PLACEHOLDER} 'Trade' under Spot`,
  "Click 'Next' and complete the security authentication",
  "Copy & Paste the API Key and Secret key",
  "Enter the passphrase you created on Bitget",
];
export const COINBASE_CREATE_API_KEYS_GUIDE = [
  "Turn On 'Advanced' Settings on Your Coinbase Account",
  "Go to Your 'API' Section on the Menu",
  "Click 'Create API Key'",
  "Allow 'View' and 'Trade' permissions, Skip the 'IP Whitelist' section",
  "Copy & Paste the API Keys and Secret - do not subtract any part of the keys",
];
export const HYPERLIQUID_CREATE_API_KEYS_GUIDE = [
  "In your Hyperliquid account, click on 'More' and then 'API'",
  "Provide a name for your API Wallet, click 'generate' and then 'Authorize API Wallet'",
  "In the 'Days Valid' field, choose 'MAX' to set it for 180 days",
  "Copy & paste your Private Key here",
  "Click 'Authorize' (do not skip this step)",
  "To get your Account Wallet Address, click on your account's email address/wallet address in the upper-right corner of the screen. Copy & paste your Account Wallet Address here",
];
export const BINGX_CREATE_API_KEYS_GUIDE = [
  "Login to your BingX account and make sure to complete your KYC.",
  "Go to the API Management page and click 'Create API'",
  'Choose a name for the API key (e.g., "Cornix Futures")',
  "Enable 'Read' permissions",
  `Check ${PERMISSIONS_PLACEHOLDER} Permissions`,
  "Confirm and generate the keys",
  "Copy and paste the API Key and API Secret",
];

export const PRIMARY_EXCHANGE_TO_CREATE_API_KEYS_GUIDE: Record<
  string,
  string[]
> = {
  [PRIMARY_EXCHANGES.BINANCE]: BINANCE_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BYBIT]: BYBIT_SPOT_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.KUCOIN]: KUCOIN_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.OKX]: OKX_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BITMEX]: BITMEX_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.HUOBI]: HUOBI_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BITGET]: BITGET_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.COINBASE]: COINBASE_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.HYPERLIQUID]: HYPERLIQUID_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BINGX]: BINGX_CREATE_API_KEYS_GUIDE,
};

export const EXTRA_INFO_TO_TITLE = {
  [ExtraInfo.WHY_NEED]: EXTERNAL_ACCOUNT_WHY_CONNECT_TITLE,
  [ExtraInfo.UUID]: WHAT_IS_UUID_TITLE,
};

export const EXTRA_INFO_TO_DESCRIPTION = {
  [ExtraInfo.WHY_NEED]: EXTERNAL_ACCOUNT_WHY_CONNECT_DESCRIPTION,
  [ExtraInfo.UUID]: WHAT_IS_UUID_DESCRIPTION,
};

export const MAX_VERIFICATION_CODE_WIDTH = 370;

export const CONNECT_TELEGRAM_INFO =
  "Connecting your Telegram account will allow us to detect which channels you are a member of.";

export const BINANCE_API_KEYS_PATH =
  "https://www.binance.com/en/my/settings/api-management";

export const USD_PRECISION = 2;

export const QUICK_CONNECT_API_EXISTS_ERROR_CODE = 409;
export const CREATE_ACCOUNT_ERROR_DEFAULT_TITLE = "Create Account Error";

export const CONNECT_DEMO_ACCOUNT_EXTRA_INFO_TITLE =
  "Trade Risk-Free with a Demo Account";
export const CONNECT_DEMO_ACCOUNT_EXTRA_INFO_CONTENT =
  "Our Demo Accounts lets you practice trading with virtual funds, so you can learn and test strategies without risking real funds. It’s the perfect way to gain real market experience and sharpen your skills before trading with real money.";
export const CONNECT_DEMO_ACCOUNT_EXTRA_INFO_ARTICLE_ID = "9853159";
